import { registerComponent, loading, createMessage, debounce } from "helpers";
import { ProductForm } from "product-form";

registerComponent(
  "donation-form",
  class DonationForm extends ProductForm {
    constructor() {
      super();

      this.presetAmountInputs = this.querySelectorAll(
        'input[type="radio"][name="id"]:not([data-custom-donation-radio])'
      );
      this.customRadio = this.querySelector("[data-custom-donation-radio]");
      this.customAmountInput = this.querySelector(
        '[name="properties[_amount]"]'
      );
      this.customProductInput = this.querySelector(
        "[data-custom-donation-product]"
      );

      this.hasUncheckedButtons = false;
      this.customAmountHandler = debounce(
        this.handleCustomAmountInput.bind(this),
        150
      );

      this.bindEvents();
      this.afterInteractionUpdate();
    }

    bindEvents() {
      this.presetAmountInputs.forEach((input) => {
        input.addEventListener(
          "change",
          this.handlePresetAmountChange.bind(this)
        );
      });

      if (this.customRadio) {
        this.customRadio.addEventListener(
          "change",
          this.handleCustomRadioChange.bind(this)
        );
      }

      if (this.customAmountInput) {
        this.customAmountInput.addEventListener(
          "input",
          this.customAmountHandler
        );
      }
    }

    handlePresetAmountChange(event) {
      if (event.target.checked && this.customAmountInput) {
        this.customAmountInput.value = "";
        this.hasUncheckedButtons = false;
      }
      this.afterInteractionUpdate();

      // Tiles mode: selecting a preset amount adds it to the cart directly
      // ("Selecting an amount will add your donation to your cart").
      if (this.customRadio && event.target.checked) {
        this.autoAdd();
      }
    }

    async autoAdd() {
      if (this.isAdding || !this.formIsValid) {
        return;
      }

      this.isAdding = true;
      loading.add(this);

      try {
        await this.addToCart();
      } finally {
        this.isAdding = false;
      }
    }

    handleCustomRadioChange() {
      this.afterInteractionUpdate();

      if (this.customRadio.checked && this.customAmountInput) {
        this.customAmountInput.focus();
      }
    }

    handleCustomAmountInput() {
      // Compact mode only: typing an amount deselects the preset radios.
      // In tiles mode the input is only enabled while the custom radio is
      // checked, so the radio group already handles exclusivity.
      if (this.customRadio) {
        this.afterInteractionUpdate();
        return;
      }

      if (this.customAmountInput.value.trim() && !this.hasUncheckedButtons) {
        this.presetAmountInputs.forEach((input) => {
          input.checked = false;
        });
        this.hasUncheckedButtons = true;
      } else if (!this.customAmountInput.value.trim()) {
        this.hasUncheckedButtons = false;
      }

      this.afterInteractionUpdate();
    }

    get formData() {
      return new FormData(this.form);
    }

    get isCustomDonationActive() {
      if (this.customRadio) {
        return this.customRadio.checked;
      }

      return Boolean(this.customProductInput && this.customAmountInput?.value);
    }

    // Whole dollars only; the custom donation product is priced at $1 and the
    // entered amount becomes the line quantity, so it must be an integer >= 1.
    get customAmount() {
      const value = this.customAmountInput?.value.trim();

      if (!value || !/^\d+$/.test(value)) {
        return null;
      }

      const amount = Number.parseInt(value, 10);

      return amount >= 1 ? amount : null;
    }

    get formIsValid() {
      return this.formData.get("id") || this.customAmountInput?.value;
    }

    afterInteractionUpdate() {
      this.toggleCustomDonation();
      this.toggleCustomAmountInput();
    }

    handleSubmit(event) {
      event.preventDefault();

      if (!this.formIsValid) {
        createMessage(this.config.validationMessage);
        return;
      }

      if (this.isCustomDonationActive && !this.customAmount) {
        createMessage(
          this.config.validationAmountMessage || this.config.validationMessage
        );
        return;
      }

      loading.add(this);
      this.afterInteractionUpdate();
      this.addToCart();
    }

    getBody() {
      const body = super.getBody();

      if (this.isCustomDonationActive) {
        // $1 product x amount = donation total. _amount is kept for cart
        // display and as the donation totals app's hook.
        body.items[0].quantity = this.customAmount;
        body.items[0].properties._amount = `$${this.customAmount}`;
      } else {
        body.items[0].quantity = 1;
        delete body.items[0].properties._amount;
      }

      return body;
    }

    // Compact mode: the custom product id lives in a hidden input that is
    // only enabled while an amount is entered.
    toggleCustomDonation() {
      if (!this.customProductInput) {
        return;
      }

      if (this.customAmountInput?.value) {
        this.presetAmountInputs.forEach((input) => {
          input.checked = false;
        });
        this.customProductInput.disabled = false;
      } else {
        this.customProductInput.disabled = true;
      }
    }

    // Tiles mode: the amount input is only enabled while the custom radio is
    // checked, keeping properties[_amount] out of preset submissions.
    toggleCustomAmountInput() {
      if (!this.customRadio || !this.customAmountInput) {
        return;
      }

      if (this.customRadio.checked) {
        this.customAmountInput.disabled = false;
      } else {
        this.customAmountInput.value = "";
        this.customAmountInput.disabled = true;
      }
    }
  }
);
